import { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import { loginApi, logoutApi, refreshApi, getStoredTokens, clearStoredTokens, getStoredRememberMePreference, validateToken } from "@/services/api/services/auth";
import { setAuthToken, set401Callback, clear401Callback } from "@/services/api/http/api-client";
import { authTimerManager } from "@/utils/auth-timer-manager";
import { authStorage } from "@/utils/auth-storage";
import { authRefreshQueue } from "@/utils/auth-refresh-queue";

// Import the storage key constant
const ACCESS_TOKEN_KEY = "finpay-token";
const STORAGE_KEY = "finpay-auth";
const REMEMBER_KEY = "finpay-remember";

// Constants for timer management
const REFRESH_LEAD_TIME = 5 * 60 * 1000; // 5 minutes before expiry
const WARNING_LEAD_TIME = 15 * 60 * 1000; // 15 minutes before expiry
const MIN_SESSION_TIME = 5 * 60 * 1000; // Minimum 5 minutes for warning

type Role = "superadmin" | "merchant";

type User = {
  id: string;
  email: string;
  name: string;
  fullName?: string;
  role: Role;
  merchantId?: string;
};

type AuthState = {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isSessionExpiring: boolean;
  rememberMe: boolean;
  login: (email: string, password: string, rememberMe?: boolean, mfaCode?: string) => Promise<{ role: Role } | null>;
  logout: () => Promise<void>;
  forcedLogout: (reason?: string) => Promise<void>;
  setRole: (role: Role) => void;
  extendSession: () => Promise<boolean>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

// Tab ID for cross-tab synchronization
const generateTabId = () => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const currentTabId = generateTabId();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSessionExpiring, setIsSessionExpiring] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Refs to prevent stale closures
  const isInitializedRef = useRef(false);
  const isLoggingOutRef = useRef(false);
  const lastTokenRef = useRef<string | null>(null);

  // Clear all timers when component unmounts
  useEffect(() => {
    return () => {
      authTimerManager.clearAllTimers();
      authRefreshQueue.clearQueue();
    };
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      if (isInitializedRef.current) return;

      try {
        // Get stored tokens and preferences using enhanced storage
        const tokenResult = authStorage.getItem<string>(ACCESS_TOKEN_KEY);
        const refreshTokenResult = authStorage.getItem<string>('finpay-refresh');
        const rememberResult = authStorage.getItem<boolean>(REMEMBER_KEY);
        const userResult = authStorage.getItem<{ user: User; token: string }>(STORAGE_KEY);

        const accessToken = tokenResult.data;
        const refreshToken = refreshTokenResult.data;
        const storedRememberMe = rememberResult.data ?? false;
        const storedUserData = userResult.data;

        console.log('Auth init - Tokens found:', {
          accessToken: accessToken ? 'YES' : 'NO',
          refreshToken: refreshToken ? 'YES' : 'NO',
          usedFallback: tokenResult.usedFallback || refreshTokenResult.usedFallback
        });

        if (accessToken) {
          setToken(accessToken);
          lastTokenRef.current = accessToken;
          setAuthToken(accessToken);
          setRememberMe(storedRememberMe);

          // Schedule token refresh
          scheduleRefresh(accessToken, storedRememberMe);

          // Try to get user data
          if (storedUserData?.user) {
            setUser(storedUserData.user);
          } else {
            // Validate token with backend
            try {
              const validatedUser = await validateToken();
              if (validatedUser?.user) {
                const userData = validatedUser.user;
                const user: User = {
                  id: userData.id,
                  email: userData.email,
                  name: userData.fullName || userData.email.split("@")[0],
                  fullName: userData.fullName,
                  role: mapRole(userData.role),
                  merchantId: userData.merchantId,
                };
                setUser(user);

                // Store user data for future
                authStorage.setItem(STORAGE_KEY, { user, token: accessToken });
              } else {
                // Token invalid, clear everything
                performLocalLogout();
              }
            } catch (tokenError) {
              console.error('Token validation failed:', tokenError);
              performLocalLogout();
            }
          }
        }

        setInitialized(true);
        isInitializedRef.current = true;
      } catch (error) {
        console.error('Auth initialization error:', error);
        performLocalLogout();
        setInitialized(true);
        isInitializedRef.current = true;
      }
    };

    // Set up 401 callback with debouncing
    const debouncedLogout = debounce(() => {
      if (!isLoggingOutRef.current) {
        handleForcedLogout('Your session has expired. Please log in again.');
      }
    }, 1000);

    set401Callback(debouncedLogout);
    initializeAuth();

    // Cleanup
    return () => {
      clear401Callback();
    };
  }, []);

  // Sync auth state to storage with validation
  useEffect(() => {
    if (!isInitializedRef.current) return;

    // Validate that token and user are synchronized
    if (token && user) {
      const storageData = { user, token };
      const result = authStorage.setItem(STORAGE_KEY, storageData);

      if (!result.success) {
        console.error('Failed to store auth state:', result.error);
        // Try fallback storage
        if (!result.usedFallback) {
          authStorage.setItem(STORAGE_KEY, storageData, true);
        }
      }
    }

    // Schedule refresh when token changes
    if (token !== lastTokenRef.current) {
      lastTokenRef.current = token;
      scheduleRefresh(token, rememberMe);
    }
  }, [user, token, rememberMe]);

  // Enhanced JWT decoder with validation
  const decodeJwt = useCallback((jwt: string): any | null => {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT structure');
        return null;
      }

      // Decode payload
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(payload);
      const decoded = JSON.parse(json);

      // Basic validation
      if (!decoded.exp || !decoded.iat) {
        console.warn('JWT missing required claims');
        return null;
      }

      // Check if token is already expired
      const now = Math.floor(Date.now() / 1000);
      if (decoded.exp <= now) {
        console.warn('JWT is already expired');
        return null;
      }

      return decoded;
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  }, []);

  // Schedule token refresh with proper timer management
  const scheduleRefresh = useCallback((jwt: string | null, rememberMeValue = false) => {
    // Clear existing timers
    authTimerManager.clearTimer('token-refresh');
    authTimerManager.clearTimer('session-warning');

    if (!jwt || isRefreshing) return;

    const payload = decodeJwt(jwt);
    if (!payload?.exp) {
      console.warn('Cannot schedule refresh: no expiry in token');
      return;
    }

    const now = Date.now();
    const exp = payload.exp * 1000;
    const timeUntilExpiry = exp - now;

    // Don't schedule if token expires too soon
    if (timeUntilExpiry < MIN_SESSION_TIME) {
      console.warn('Token expires too soon, triggering refresh immediately');
      performTokenRefresh(rememberMeValue);
      return;
    }

    const refreshDelay = Math.max(0, exp - now - REFRESH_LEAD_TIME);
    const warningDelay = Math.max(0, exp - now - WARNING_LEAD_TIME);

    // Schedule session warning for ALL users (not just when rememberMe is false)
    if (warningDelay > 0) {
      authTimerManager.setTimer('session-warning', () => {
        console.log('Session expiring soon, showing warning');
        setIsSessionExpiring(true);
      }, warningDelay);
    }

    // Schedule token refresh
    authTimerManager.setTimer('token-refresh', () => {
      setIsSessionExpiring(false);
      performTokenRefresh(rememberMeValue);
    }, refreshDelay);

    console.log('Scheduled refresh in', Math.round(refreshDelay / 1000), 'seconds');
  }, [isRefreshing, decodeJwt]);

  // Enhanced token refresh with retry mechanism
  const performTokenRefresh = useCallback(async (rememberMeValue = false, retryCount = 0): Promise<void> => {
    if (isRefreshing || isLoggingOutRef.current) return;

    setIsRefreshing(true);

    try {
      // Use the refresh queue to prevent race conditions
      const refreshed = await authRefreshQueue.refreshToken();

      if (refreshed?.accessToken) {
        console.log('Token refreshed successfully');
        setToken(refreshed.accessToken);
        setAuthToken(refreshed.accessToken);

        // Store new tokens using enhanced storage
        authStorage.setItem(ACCESS_TOKEN_KEY, refreshed.accessToken);
        if (refreshed.refreshToken) {
          authStorage.setItem('finpay-refresh', refreshed.refreshToken);
        }

        // Schedule next refresh
        scheduleRefresh(refreshed.accessToken, rememberMeValue);
      } else {
        console.warn('Token refresh returned null');
        await performLogout();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);

      // Implement retry mechanism with exponential backoff
      const maxRetries = 3;
      if (retryCount < maxRetries && error instanceof Error) {
        const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff

        console.log(`Retrying refresh in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(() => {
          performTokenRefresh(rememberMeValue, retryCount + 1);
        }, delay);
      } else {
        // Max retries reached, perform logout
        await performLogout();
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, scheduleRefresh]);

  // Extend session functionality
  const extendSession = useCallback(async (): Promise<boolean> => {
    try {
      console.log('Extending session manually');
      setIsSessionExpiring(false);
      await performTokenRefresh(rememberMe);
      return true;
    } catch (error) {
      console.error('Failed to extend session:', error);
      return false;
    }
  }, [rememberMe, performTokenRefresh]);

  // Local logout without API call
  const performLocalLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    setRememberMe(false);
    setAuthToken(null);
    setIsSessionExpiring(false);
    isLoggingOutRef.current = true;

    // Clear timers
    authTimerManager.clearAllTimers();

    // Clear storage
    authStorage.clearAuthData([ACCESS_TOKEN_KEY, 'finpay-refresh', STORAGE_KEY, REMEMBER_KEY]);

    // Clear refresh queue
    authRefreshQueue.clearQueue();

    // Clear 401 callback
    clear401Callback();
  }, []);

  // Full logout with API call
  const performLogout = useCallback(async () => {
    try {
      const result = await logoutApi();
      console.log('Logout result:', result);
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      // Always clear local state
      performLocalLogout();
    }
  }, [performLocalLogout]);

  // Handle forced logout (e.g., 401 response)
  const handleForcedLogout = useCallback(async (reason?: string) => {
    if (isLoggingOutRef.current) return;

    console.log('Forced logout:', reason);
    performLocalLogout();

    // Show reason if provided
    if (reason) {
      console.error('Logout reason:', reason);
    }

    // Redirect to login
    setTimeout(() => {
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }, 100);
  }, [performLocalLogout]);

  // Login function with enhanced error handling
  const login = useCallback(async (email: string, password: string, rememberMeValue = true, mfaCode?: string) => {
    if (!email || !password) return null;

    try {
      const data = await loginApi(email, password, rememberMeValue, mfaCode);
      const role = mapRole(data.user.role);

      const newUser: User = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.fullName || data.user.email.split("@")[0],
        fullName: data.user.fullName,
        role,
        merchantId: data.user.merchantId,
      };

      // Update state
      setUser(newUser);
      setToken(data.accessToken);
      setRememberMe(rememberMeValue);
      setAuthToken(data.accessToken);
      isLoggingOutRef.current = false;

      // Store using enhanced storage
      authStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      if (data.refreshToken) {
        authStorage.setItem('finpay-refresh', data.refreshToken);
      }
      authStorage.setItem(REMEMBER_KEY, rememberMeValue);
      authStorage.setItem(STORAGE_KEY, { user: newUser, token: data.accessToken });

      // Schedule refresh
      scheduleRefresh(data.accessToken, rememberMeValue);

      // Notify other tabs with proper synchronization
      const storageData = { user: newUser, token: data.accessToken };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));

      // Also dispatch storage event for immediate notification
      window.dispatchEvent(new StorageEvent('storage', {
        key: STORAGE_KEY,
        newValue: JSON.stringify(storageData),
        oldValue: null,
        storageArea: localStorage
      }));

      return { role };
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [scheduleRefresh]);

  // Logout function
  const logout = useCallback(async () => {
    await performLogout();
  }, [performLogout]);

  // Forced logout function
  const forcedLogout = useCallback(async (reason?: string) => {
    await handleForcedLogout(reason);
  }, [handleForcedLogout]);

  // Set role function
  const setRole = useCallback((role: Role) => {
    if (!user) return;
    const updatedUser = { ...user, role };
    setUser(updatedUser);

    // Update stored user data
    authStorage.setItem(STORAGE_KEY, { user: updatedUser, token });
  }, [user, token]);

  // Cross-tab synchronization with proper conflict resolution
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACCESS_TOKEN_KEY || e.key === STORAGE_KEY) {
        console.log(`[Tab ${currentTabId}] Storage changed in another tab`);

        if (e.newValue === null) {
          // User logged out in another tab
          console.log('Logout detected in another tab');
          performLocalLogout();
        } else {
          // User logged in or tokens updated in another tab
          try {
            const tokenResult = authStorage.getItem<string>(ACCESS_TOKEN_KEY);
            const userResult = authStorage.getItem<{ user: User; token: string }>(STORAGE_KEY);

            if (tokenResult.data && userResult.data?.user) {
              const userData = userResult.data.user;

              setUser(userData);
              setToken(tokenResult.data);
              setAuthToken(tokenResult.data);
              setRememberMe(getStoredRememberMePreference());

              scheduleRefresh(tokenResult.data, getStoredRememberMePreference());
            }
          } catch (error) {
            console.error('Failed to sync auth state from other tab:', error);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    console.log(`[Tab ${currentTabId}] Storage listener setup complete`);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [performLocalLogout, scheduleRefresh]);

  // Helper functions
  const mapRole = (external?: string): Role => {
    switch ((external || "").toUpperCase()) {
      case "ADMIN":
      case "SUPERADMIN":
        return "superadmin";
      case "MERCHANT_OWNER":
      case "MERCHANT":
        return "merchant";
      default:
        return "merchant";
    }
  };

  // Debounce utility function
  function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  // Memoized value
  const value = useMemo<AuthState>(
    () => ({
      user,
      token,
      isAuthenticated: !!user && !!token,
      isInitialized: initialized,
      isSessionExpiring,
      rememberMe,
      login,
      logout,
      forcedLogout,
      setRole,
      extendSession
    }),
    [user, token, initialized, isSessionExpiring, isRefreshing, rememberMe, login, logout, forcedLogout, setRole, extendSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function useRole(): Role | null {
  const { user } = useAuth();
  return user?.role ?? null;
}

export function useRoleDetection() {
  const { user } = useAuth();

  const isAdmin = user?.role === "superadmin";
  const isMerchant = user?.role === "merchant";
  const userRole = user?.role ?? null;

  return {
    isAdmin,
    isMerchant,
    userRole,
    hasRole: (role: Role) => user?.role === role
  };
}